import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FileTask, FileTaskStatus } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { useRealtime } from '../lib/realtime.js';
import { toast } from '../components/Toast.js';
import { SortableTh } from '../components/SortableTh.js';
import { useTableSort } from '../lib/sort.js';
import { RowActionsMenu } from '../components/RowActions.js';
// Lucide row actions matching the look on TaggingRulesAdmin.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — installed via pnpm install
import { Check, RotateCcw, X } from 'lucide-react';

const TABS: FileTaskStatus[] = ['failed', 'complete', 'started', 'queued'];

export function FileTasksPage() {
  const [tab, setTab] = useState<FileTaskStatus>('complete');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['file-tasks', tab],
    queryFn: () => apiList<FileTask>('/file-tasks', { query: { status: tab } }),
  });

  useRealtime({
    types: ['task.queued', 'task.started', 'task.completed', 'task.failed'],
    onEvent: () => qc.invalidateQueries({ queryKey: ['file-tasks'] }),
  });

  const retry = useMutation({
    mutationFn: (id: string) => apiSingle<FileTask>(`/file-tasks/${id}/retry`, { method: 'POST' }),
    onSuccess: () => { toast.success('Retry queued'); qc.invalidateQueries({ queryKey: ['file-tasks'] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/file-tasks/${id}/dismiss`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['file-tasks'] }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => apiSingle<FileTask>(`/file-tasks/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['file-tasks'] }),
  });

  const { sorted, sort, setSort } = useTableSort(data?.data, {
    name: (t: FileTask) => t.name,
    pipelineId: (t) => t.pipelineId ?? '',
    status: (t) => t.status,
    createdAt: (t) => t.createdAt,
    completedAt: (t) => t.completedAt ?? '',
    result: (t) => t.errorMessage ?? t.resultMessage ?? '',
  }, { key: 'createdAt', dir: 'desc' });

  // ctsk-068 — bulk dismiss/cancel for the visible tab.
  // The single-row mutation already validates capabilities; we just
  // dispatch a serial loop to avoid hammering the server. Could be
  // parallelized with Promise.all but for typical row counts (<100)
  // serial keeps the loop simple and the progress predictable.
  async function bulkDismiss() {
    const rows = data?.data ?? [];
    const dismissable = rows.filter(
      (t) => t.status === 'complete' || t.status === 'failed' || t.status === 'cancelled',
    );
    if (dismissable.length === 0) {
      toast.error('No dismissable tasks on this tab.');
      return;
    }
    if (!confirm(`Dismiss ${dismissable.length} ${tab} task${dismissable.length === 1 ? '' : 's'}?`)) return;
    let ok = 0;
    let failed = 0;
    for (const t of dismissable) {
      try {
        await apiFetch<void>(`/file-tasks/${t.id}/dismiss`, { method: 'POST' });
        ok++;
      } catch {
        failed++;
      }
    }
    qc.invalidateQueries({ queryKey: ['file-tasks'] });
    if (ok > 0 && failed === 0) toast.success(`Dismissed ${ok}`);
    else if (ok > 0 && failed > 0) toast.error(`Dismissed ${ok}, ${failed} failed`);
    else toast.error(`All ${failed} dismiss(es) failed`);
  }

  async function bulkCancel() {
    const rows = data?.data ?? [];
    const cancellable = rows.filter((t) => t.status === 'queued' || t.status === 'started');
    if (cancellable.length === 0) {
      toast.error('No cancellable tasks on this tab.');
      return;
    }
    if (!confirm(`Cancel ${cancellable.length} ${tab} task${cancellable.length === 1 ? '' : 's'}?`)) return;
    let ok = 0;
    let failed = 0;
    for (const t of cancellable) {
      try {
        await apiSingle<FileTask>(`/file-tasks/${t.id}/cancel`, { method: 'POST' });
        ok++;
      } catch {
        failed++;
      }
    }
    qc.invalidateQueries({ queryKey: ['file-tasks'] });
    if (ok > 0 && failed === 0) toast.success(`Cancelled ${ok}`);
    else if (ok > 0 && failed > 0) toast.error(`Cancelled ${ok}, ${failed} failed`);
    else toast.error(`All ${failed} cancel(s) failed`);
  }

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">File tasks</h1>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t}
            className={`tab ${t === tab ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {isLoading && <SkeletonRows rows={5} />}
      {data && data.data.length === 0 && (
        <EmptyState title={`No ${tab} tasks`} body="Tasks will show up here as documents are processed." />
      )}
      {data && data.data.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <SortableTh sortKey="name" sort={sort} onSortChange={setSort}>Name</SortableTh>
              <SortableTh sortKey="pipelineId" sort={sort} onSortChange={setSort}>Pipeline</SortableTh>
              <SortableTh sortKey="status" sort={sort} onSortChange={setSort}>Status</SortableTh>
              <SortableTh sortKey="createdAt" sort={sort} onSortChange={setSort}>Created</SortableTh>
              <SortableTh sortKey="completedAt" sort={sort} onSortChange={setSort}>Completed</SortableTh>
              <SortableTh sortKey="result" sort={sort} onSortChange={setSort}>Result</SortableTh>
              {/* ctsk-068 — header ellipse menu with bulk actions
                  appropriate for the current tab. */}
              <th style={{ whiteSpace: 'nowrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Actions
                  <RowActionsMenu
                    actions={
                      tab === 'queued' || tab === 'started'
                        ? [
                            { label: `Cancel all ${tab}`, destructive: true, onClick: bulkCancel },
                          ]
                        : [
                            { label: `Dismiss all ${tab}`, onClick: bulkDismiss },
                          ]
                    }
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{t.pipelineId}</code></td>
                <td><StatusBadge status={t.status} variant="task" /></td>
                <td>{new Date(t.createdAt).toLocaleString()}</td>
                <td>{t.completedAt ? new Date(t.completedAt).toLocaleString() : '—'}</td>
                <td style={{ maxWidth: 320, color: t.status === 'failed' ? 'var(--danger-primary)' : undefined }}>
                  {t.errorMessage ?? t.resultMessage ?? '—'}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
                    {t.status === 'failed' && (
                      <button
                        className="btn btn--ghost"
                        onClick={() => retry.mutate(t.id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <RotateCcw size={13} strokeWidth={1.75} aria-hidden /> Retry
                      </button>
                    )}
                    {(t.status === 'queued' || t.status === 'started') && (
                      <button
                        className="btn btn--ghost"
                        onClick={() => cancel.mutate(t.id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <X size={13} strokeWidth={1.75} aria-hidden /> Cancel
                      </button>
                    )}
                    {(t.status === 'complete' || t.status === 'failed' || t.status === 'cancelled') && (
                      <button
                        className="btn btn--ghost"
                        onClick={() => dismiss.mutate(t.id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Check size={13} strokeWidth={1.75} aria-hidden /> Dismiss
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
